import { useEffect, useState, useCallback } from "react";
import { api } from "../../api";
import { PanelHeading } from "./PortalShell";
import {
  Loader2,
  Inbox,
  Mail,
  Newspaper,
  MessageSquare,
  Download,
  FileText,
  Reply,
  ArrowLeft,
  RefreshCw,
  Send,
  Circle,
} from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export const NotificationsPanel = ({ onRead }) => {
  const [tab, setTab] = useState("email");

  const [emails, setEmails] = useState([]);
  const [notifications, setNotifications] = useState([]);

  const [selectedEmail, setSelectedEmail] = useState(null);
  const [replyBody, setReplyBody] = useState("");

  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const loadEmails = useCallback(async () => {
    const { data } = await api.get("/email/messages");
    setEmails(data);
  }, []);

  const loadNotifications = useCallback(async () => {
    const { data } = await api.get("/notifications");
    setNotifications(data);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      await Promise.all([
        loadEmails(),
        loadNotifications(),
      ]);
    } catch (err) {
      console.error(err);
      setError("The inbox could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [loadEmails, loadNotifications]);

  useEffect(() => {
    load();
  }, [load]);

  const openEmail = async (email) => {
    setOpening(true);
    setError("");

    try {
      const { data } = await api.get(`/email/messages/${email.id}`);
      setSelectedEmail(data);

      if (!data.read) {
        await api.post(`/email/messages/${email.id}/read`);

        setEmails((current) =>
          current.map((item) =>
            item.id === email.id ? { ...item, read: true } : item
          )
        );

        setSelectedEmail((current) =>
          current ? { ...current, read: true } : current
        );

        onRead && onRead();
      }
    } catch (err) {
      console.error(err);
      setError("That email could not be opened.");
    } finally {
      setOpening(false);
    }
  };

  const sendReply = async () => {
    if (!selectedEmail || !replyBody.trim()) return;

    setSending(true);
    setError("");

    try {
      const { data } = await api.post(
        `/email/messages/${selectedEmail.id}/reply`,
        {
          body: replyBody.trim(),
        }
      );

      setReplyBody("");

      if (data.thread) {
        setSelectedEmail(data.thread);
      } else {
        const refreshed = await api.get(
          `/email/messages/${selectedEmail.id}`
        );
        setSelectedEmail(refreshed.data);
      }

      await loadEmails();
    } catch (err) {
      console.error(err);
      setError("The reply could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const markNotificationRead = async (notification) => {
    if (notification.read) return;

    try {
      await api.post(`/notifications/${notification.id}/read`);

      setNotifications((current) =>
        current.map((item) =>
          item.id === notification.id
            ? { ...item, read: true }
            : item
        )
      );

      onRead && onRead();
    } catch (err) {
      console.error(err);
    }
  };

  const emailPreview = (email) => {
    const text =
      email.preview ||
      email.text ||
      email.body ||
      "";

    return text.length > 150
      ? `${text.slice(0, 150)}…`
      : text;
  };

  const mailboxLabel = (value) => {
    if (!value) return "Squadron";

    const address = Array.isArray(value)
      ? value[0]
      : value;

    return address
      .split("@")[0]
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div>
      <PanelHeading
        title="Inbox"
        intro="Squadron email, messages, notifications and newsletters."
      />

      <div className="flex gap-2 mb-5 border-b border-raf-sky">
        <button
          type="button"
          onClick={() => {
            setTab("email");
            setSelectedEmail(null);
          }}
          className={`px-4 py-3 font-semibold flex items-center gap-2 ${
            tab === "email"
              ? "bg-raf-blue text-white"
              : "bg-white text-raf-blue"
          }`}
        >
          <Mail size={16} />
          Email
          {emails.filter((e) => !e.read).length > 0 && (
            <span className="bg-raf-red text-white min-w-5 h-5 px-1.5 text-[10px] flex items-center justify-center rounded-full">
              {emails.filter((e) => !e.read).length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            setTab("squadron");
            setSelectedEmail(null);
          }}
          className={`px-4 py-3 font-semibold flex items-center gap-2 ${
            tab === "squadron"
              ? "bg-raf-blue text-white"
              : "bg-white text-raf-blue"
          }`}
        >
          <MessageSquare size={16} />
          Squadron messages
          {notifications.filter((n) => !n.read).length > 0 && (
            <span className="bg-raf-red text-white min-w-5 h-5 px-1.5 text-[10px] flex items-center justify-center rounded-full">
              {notifications.filter((n) => !n.read).length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={load}
          className="ml-auto px-3 text-raf-blue hover:bg-raf-sky"
          title="Refresh inbox"
        >
          <RefreshCw size={17} />
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border-l-4 border-raf-red text-sm text-raf-red">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-raf-slate p-10 justify-center">
          <Loader2 className="animate-spin" />
          Loading...
        </div>
      ) : tab === "email" ? (
        selectedEmail ? (
          <EmailView
            email={selectedEmail}
            opening={opening}
            replyBody={replyBody}
            setReplyBody={setReplyBody}
            sendReply={sendReply}
            sending={sending}
            onBack={() => setSelectedEmail(null)}
            mailboxLabel={mailboxLabel}
          />
        ) : emails.length === 0 ? (
          <div className="bg-white p-10 text-center text-raf-slate border border-white">
            <Inbox className="mx-auto mb-2 text-raf-sky" />
            No email has arrived yet.
          </div>
        ) : (
          <div className="bg-white border border-raf-sky divide-y divide-raf-sky">
            {emails.map((email) => (
              <button
                type="button"
                key={email.id}
                onClick={() => openEmail(email)}
                className={`w-full text-left p-4 hover:bg-[#F3F8FA] transition-colors ${
                  !email.read ? "bg-[#F8FCFE]" : "bg-white"
                }`}
              >
                <div className="flex gap-3 items-start">
                  <div className="pt-1 w-5 shrink-0">
                    {!email.read ? (
                      <Circle
                        size={10}
                        fill="currentColor"
                        className="text-raf-red"
                      />
                    ) : null}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div
                          className={`truncate ${
                            !email.read
                              ? "font-bold text-raf-navy"
                              : "font-semibold text-raf-navy"
                          }`}
                        >
                          {email.from || "Unknown sender"}
                        </div>

                        <div
                          className={`truncate mt-0.5 ${
                            !email.read
                              ? "font-semibold text-raf-navy"
                              : "text-raf-slate"
                          }`}
                        >
                          {email.subject || "(No subject)"}
                        </div>
                      </div>

                      <div className="text-xs text-raf-slate whitespace-nowrap">
                        {new Date(email.created_at).toLocaleString("en-GB")}
                      </div>
                    </div>

                    <p className="text-sm text-raf-slate mt-1 line-clamp-2">
                      {emailPreview(email)}
                    </p>

                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-[10px] uppercase bg-raf-sky text-raf-blue px-2 py-0.5">
                        {mailboxLabel(email.mailbox || email.to)}
                      </span>

                      {email.attachments?.length > 0 && (
                        <span className="text-[10px] uppercase text-raf-slate">
                          {email.attachments.length} attachment
                          {email.attachments.length !== 1 ? "s" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )
      ) : notifications.length === 0 ? (
        <div className="bg-white p-10 text-center text-raf-slate border border-white">
          <Inbox className="mx-auto mb-2 text-raf-sky" />
          Nothing in your squadron inbox yet.
        </div>
      ) : (
        <div
          className="space-y-3"
          data-testid="notifications-list"
        >
          {notifications.map((n) => {
            const Icon =
              n.kind === "newsletter"
                ? Newspaper
                : n.kind === "document"
                ? FileText
                : MessageSquare;

            return (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                onClick={() => markNotificationRead(n)}
                className={`bg-white border border-white p-5 border-l-4 cursor-pointer ${
                  n.read
                    ? "border-l-raf-sky"
                    : "border-l-raf-red"
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-raf-sky text-raf-blue shrink-0">
                    <Icon size={16} />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-display font-bold text-raf-navy">
                        {n.title}
                      </h3>

                      {!n.read && (
                        <span className="text-[10px] uppercase bg-raf-red text-white px-2 py-0.5">
                          New
                        </span>
                      )}

                      {n.kind === "newsletter" && (
                        <span className="text-[10px] uppercase bg-raf-blue text-white px-2 py-0.5">
                          Newsletter
                        </span>
                      )}

                      {n.channels?.includes("email") && (
                        <span className="text-[10px] uppercase bg-raf-sky text-raf-blue px-2 py-0.5 flex items-center gap-1">
                          <Mail size={10} />
                          Emailed
                        </span>
                      )}
                    </div>

                    <p className="mt-2 text-raf-slate leading-relaxed whitespace-pre-line text-sm">
                      {n.body}
                    </p>

                    {Array.isArray(n.links) &&
                    n.links.length > 0 ? (
                      <div
                        className="mt-3 flex flex-wrap gap-2"
                        data-testid={`notification-links-${n.id}`}
                      >
                        {n.links.map((l, i) => (
                          <a
                            key={`${n.id}-${i}`}
                            href={
                              (l.url || "").startsWith("/api")
                                ? `${BASE_URL}${l.url}`
                                : l.url
                            }
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-raf-red text-white hover:bg-[#A00926] transition-colors"
                          >
                            <Download size={14} />
                            {l.label || "Open"}
                          </a>
                        ))}
                      </div>
                    ) : n.link ? (
                      <a
                        href={
                          n.link.startsWith("/api")
                            ? `${BASE_URL}${n.link}`
                            : n.link
                        }
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-raf-red text-white hover:bg-[#A00926] transition-colors"
                      >
                        <Download size={14} />
                        {n.link_label || "Open"}
                      </a>
                    ) : null}

                    <div className="mt-3 text-xs text-raf-slate">
                      {n.from_name} &middot;{" "}
                      {new Date(n.created_at).toLocaleString("en-GB")}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EmailView = ({
  email,
  opening,
  replyBody,
  setReplyBody,
  sendReply,
  sending,
  onBack,
  mailboxLabel,
}) => {
  const conversation =
    Array.isArray(email.messages) && email.messages.length
      ? email.messages
      : [email];

  return (
    <div className="bg-white border border-raf-sky">
      <div className="p-4 border-b border-raf-sky flex items-center gap-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm font-semibold text-raf-blue hover:text-raf-red"
        >
          <ArrowLeft size={16} />
          Inbox
        </button>

        {opening && (
          <Loader2
            size={16}
            className="animate-spin text-raf-slate"
          />
        )}
      </div>

      <div className="p-5 border-b border-raf-sky">
        <h2 className="font-display font-bold text-xl text-raf-navy">
          {email.subject || "(No subject)"}
        </h2>

        <div className="mt-3 text-sm text-raf-slate">
          <div>
            <strong>From:</strong> {email.from}
          </div>

          <div>
            <strong>To:</strong>{" "}
            {Array.isArray(email.to)
              ? email.to.join(", ")
              : email.to}
          </div>

          <div>
            <strong>Mailbox:</strong>{" "}
            {mailboxLabel(email.mailbox || email.to)}
          </div>
        </div>
      </div>

      <div className="divide-y divide-raf-sky">
        {conversation.map((message, index) => (
          <div
            key={message.id || `${email.id}-${index}`}
            className="p-5"
          >
            <div className="flex justify-between gap-4 mb-3">
              <div>
                <div className="font-semibold text-raf-navy">
                  {message.direction === "outbound"
                    ? message.from_name ||
                      "1471 Horwich Squadron"
                    : message.from}
                </div>

                <div className="text-xs text-raf-slate">
                  {message.direction === "outbound"
                    ? "Sent"
                    : "Received"}
                </div>
              </div>

              <div className="text-xs text-raf-slate whitespace-nowrap">
                {message.created_at
                  ? new Date(
                      message.created_at
                    ).toLocaleString("en-GB")
                  : ""}
              </div>
            </div>

            {message.html ? (
              <div
                className="text-sm leading-relaxed"
                dangerouslySetInnerHTML={{
                  __html: message.html,
                }}
              />
            ) : (
              <div className="text-sm leading-relaxed whitespace-pre-wrap">
                {message.text ||
                  message.body ||
                  "(No message body)"}
              </div>
            )}

            {message.attachments?.length > 0 && (
              <div className="mt-4">
                <div className="font-semibold text-sm text-raf-navy mb-2">
                  Attachments
                </div>

                <div className="flex flex-wrap gap-2">
                  {message.attachments.map((attachment) => (
                    <a
                      key={attachment.id}
                      href={`${BASE_URL}/api/email/messages/${message.id}/attachments/${attachment.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 bg-raf-sky text-raf-blue text-sm"
                    >
                      <Download size={14} />
                      {attachment.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="p-5 bg-[#F3F8FA] border-t border-raf-sky">
        <div className="flex items-center gap-2 font-semibold text-raf-navy mb-3">
          <Reply size={16} />
          Reply
        </div>

        <textarea
          rows={6}
          value={replyBody}
          onChange={(e) => setReplyBody(e.target.value)}
          placeholder="Write your reply..."
          className="w-full border border-raf-sky bg-white p-3 text-sm outline-none focus:border-raf-blue"
        />

        <div className="flex justify-end mt-3">
          <button
            type="button"
            disabled={sending || !replyBody.trim()}
            onClick={sendReply}
            className="inline-flex items-center gap-2 bg-raf-red text-white px-5 py-2.5 font-semibold disabled:opacity-50"
          >
            {sending ? (
              <Loader2
                size={16}
                className="animate-spin"
              />
            ) : (
              <Send size={16} />
            )}

            Send reply
          </button>
        </div>
      </div>
    </div>
  );
};